import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Mail, Calendar, FolderOpen, FileSpreadsheet, FileText, 
  Sparkles, AlertCircle
} from 'lucide-react';
import GmailPanel from './GmailPanel';
import CalendarPanel from './CalendarPanel';
import AIAssistantPanel from './AIAssistantPanel';
import * as api from '../services/api';

const GoogleToolsPage = ({ currentUser, globalSearch = '' }) => {
  const [activeTab, setActiveTab] = useState('ai');
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [statusRes, empresasRes, tareasRes, templatesRes] = await Promise.all([
          api.getGoogleStatus(currentUser.id),
          api.getEmpresas(),
          api.getTasks(),
          api.getEmailTemplates()
        ]);
        
        setConnectionStatus(statusRes.data);
        setEmpresas(empresasRes.data || []);
        setTareas(tareasRes.data || []);
        setEmailTemplates(templatesRes.data || []);
        
        // Extract all contacts from empresas
        const allContacts = [];
        (empresasRes.data || []).forEach(empresa => {
          if (empresa.contactos) {
            empresa.contactos.forEach(c => {
              allContacts.push({ ...c, empresa_name: empresa.name });
            });
          }
        });
        setContactos(allContacts);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [currentUser.id]);

  const userConnected = connectionStatus?.user_connection?.connected;
  const companyConnected = connectionStatus?.company_connection?.connected;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  if (!userConnected && !companyConnected) {
    return (
      <Card className="max-w-lg mx-auto mt-12">
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Google no conectado</h2>
          <p className="text-gray-600 mb-6">
            Para usar las herramientas de Google, primero debes conectar tu cuenta.
          </p>
          <a 
            href="/admin" 
            className="inline-flex items-center px-4 py-2 bg-[#D4A843] text-white rounded-lg hover:bg-[#c49a3a]"
          >
            Ir a Configuración
          </a>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Herramientas de Google</h1>
        <p className="text-gray-600">
          Gestiona tu email, calendario y archivos directamente desde el CRM
        </p>
        <div className="flex gap-2 mt-3">
          {userConnected && (
            <Badge className="bg-green-100 text-green-800">
              Gmail y Calendar conectados
            </Badge>
          )}
          {companyConnected && (
            <Badge className="bg-blue-100 text-blue-800">
              Drive, Sheets y Docs conectados
            </Badge>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl mb-6">
          <TabsTrigger 
            value="ai" 
            className="flex items-center gap-2"
          >
            <Sparkles className="h-4 w-4" />
            IA
          </TabsTrigger>
          <TabsTrigger 
            value="gmail" 
            disabled={!userConnected}
            className="flex items-center gap-2"
          >
            <Mail className="h-4 w-4" />
            Gmail
          </TabsTrigger>
          <TabsTrigger 
            value="calendar" 
            disabled={!userConnected}
            className="flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            Calendar
          </TabsTrigger>
          <TabsTrigger 
            value="drive" 
            disabled={!companyConnected}
            className="flex items-center gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            Drive
          </TabsTrigger>
          <TabsTrigger 
            value="sheets" 
            disabled={!companyConnected}
            className="flex items-center gap-2"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Sheets
          </TabsTrigger>
          <TabsTrigger 
            value="docs" 
            disabled={!companyConnected}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Docs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai">
          <AIAssistantPanel 
            currentUser={currentUser}
            crmContext={{
              empresas_count: empresas.length,
              tareas_pendientes: tareas.filter(t => t.status === 'pending').length,
              user_name: currentUser.name
            }}
          />
        </TabsContent>

        <TabsContent value="gmail">
          {userConnected ? (
            <GmailPanel 
              currentUser={currentUser} 
              empresas={empresas}
              contactos={contactos}
              emailTemplates={emailTemplates}
            />
          ) : (
            <NotConnectedMessage service="Gmail" />
          )}
        </TabsContent>

        <TabsContent value="calendar">
          {userConnected ? (
            <CalendarPanel 
              currentUser={currentUser}
              tareas={tareas.filter(t => t.assigned_to === currentUser.name)}
            />
          ) : (
            <NotConnectedMessage service="Calendar" />
          )}
        </TabsContent>

        <TabsContent value="drive">
          {companyConnected ? (
            <DrivePanel />
          ) : (
            <NotConnectedMessage service="Drive" isCompany />
          )}
        </TabsContent>

        <TabsContent value="sheets">
          {companyConnected ? (
            <SheetsPanel empresas={empresas} />
          ) : (
            <NotConnectedMessage service="Sheets" isCompany />
          )}
        </TabsContent>

        <TabsContent value="docs">
          {companyConnected ? (
            <DocsPanel />
          ) : (
            <NotConnectedMessage service="Docs" isCompany />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const NotConnectedMessage = ({ service, isCompany }) => (
  <Card>
    <CardContent className="py-12 text-center">
      <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
      <p className="text-gray-600 mb-4">
        {isCompany 
          ? `${service} requiere conectar la cuenta de empresa.`
          : `${service} requiere conectar tu cuenta personal de Google.`
        }
      </p>
      <a href="/admin" className="text-blue-600 hover:underline">
        Ir a Admin &gt; Google para conectar
      </a>
    </CardContent>
  </Card>
);

// Placeholder components for Drive, Sheets, Docs
const DrivePanel = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await api.listDriveFiles();
      setFiles(response.data || []);
    } catch (err) {
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      await api.uploadToDrive(file);
      loadFiles();
    } catch (err) {
      console.error('Error uploading:', err);
      alert('Error subiendo archivo');
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-6 w-6 text-yellow-500" />
          <h2 className="text-xl font-semibold">Google Drive</h2>
        </div>
        <div className="flex gap-2">
          <label className="cursor-pointer">
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
            <span className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600">
              {uploading ? 'Subiendo...' : 'Subir Archivo'}
            </span>
          </label>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando archivos...</div>
          ) : files.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay archivos</div>
          ) : (
            <div className="divide-y">
              {files.map(file => (
                <div key={file.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <FolderOpen className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatSize(file.size)}</p>
                    </div>
                  </div>
                  {file.webViewLink && (
                    <a href={file.webViewLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">
                      Abrir
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SheetsPanel = ({ empresas }) => {
  const [exporting, setExporting] = useState(false);

  const exportEmpresas = async () => {
    try {
      setExporting(true);
      
      // Create a new spreadsheet
      const createRes = await api.createSpreadsheet(`CRM Export - ${new Date().toLocaleDateString()}`);
      const spreadsheetId = createRes.data.spreadsheetId;
      
      // Prepare data
      const headers = ['Nombre', 'Industria', 'Email', 'Teléfono', 'Estado', 'Creado'];
      const rows = empresas.map(e => [
        e.name || '',
        e.industry || '',
        e.email || '',
        e.phone || '',
        e.status || '',
        e.created_at ? new Date(e.created_at).toLocaleDateString() : ''
      ]);
      
      // Write data
      await api.writeSpreadsheet(spreadsheetId, 'A1', [headers, ...rows]);
      
      alert('Exportación completada! Se abrirá la hoja de cálculo.');
      window.open(createRes.data.spreadsheetUrl, '_blank');
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Error exportando datos');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-green-500" />
          <h2 className="text-xl font-semibold">Google Sheets</h2>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4">Exportar Datos del CRM</h3>
          <div className="space-y-3">
            <button 
              onClick={exportEmpresas}
              disabled={exporting}
              className="w-full p-4 border rounded-lg hover:bg-gray-50 flex items-center gap-3 text-left"
            >
              <FileSpreadsheet className="h-8 w-8 text-green-500" />
              <div>
                <p className="font-medium">Exportar Empresas</p>
                <p className="text-sm text-gray-500">{empresas.length} empresas en el CRM</p>
              </div>
            </button>
          </div>
          {exporting && (
            <p className="text-center text-gray-500 mt-4">Exportando datos...</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const DocsPanel = () => {
  const [creating, setCreating] = useState(false);

  const createDocument = async (title) => {
    try {
      setCreating(true);
      const response = await api.createDocument(title);
      window.open(response.data.webViewLink, '_blank');
    } catch (err) {
      console.error('Error creating document:', err);
      alert('Error creando documento');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-semibold">Google Docs</h2>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4">Crear Documento</h3>
          <div className="space-y-3">
            <button 
              onClick={() => createDocument(`Propuesta - ${new Date().toLocaleDateString()}`)}
              disabled={creating}
              className="w-full p-4 border rounded-lg hover:bg-gray-50 flex items-center gap-3 text-left"
            >
              <FileText className="h-8 w-8 text-blue-500" />
              <div>
                <p className="font-medium">Nueva Propuesta</p>
                <p className="text-sm text-gray-500">Crear documento de propuesta comercial</p>
              </div>
            </button>
            <button 
              onClick={() => createDocument(`Contrato - ${new Date().toLocaleDateString()}`)}
              disabled={creating}
              className="w-full p-4 border rounded-lg hover:bg-gray-50 flex items-center gap-3 text-left"
            >
              <FileText className="h-8 w-8 text-purple-500" />
              <div>
                <p className="font-medium">Nuevo Contrato</p>
                <p className="text-sm text-gray-500">Crear documento de contrato</p>
              </div>
            </button>
          </div>
          {creating && (
            <p className="text-center text-gray-500 mt-4">Creando documento...</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleToolsPage;
